import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Calendar, MapPin, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";

const DISMISS_STORAGE_KEY = "cecit-home-popup-dismissed";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function readDismissed() {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISS_STORAGE_KEY) || "{}");
    // Migrate legacy array format (permanent dismiss) to the new
    // timestamped object format so old visitors aren't stuck forever.
    if (Array.isArray(raw)) return {};
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function isDismissed(eventId) {
  const dismissed = readDismissed();
  const ts = dismissed[eventId];
  if (!ts) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
}

function markDismissed(eventId) {
  try {
    const dismissed = readDismissed();
    dismissed[eventId] = Date.now();
    // Drop stale entries so storage doesn't grow unbounded.
    const now = Date.now();
    Object.keys(dismissed).forEach((k) => {
      if (now - dismissed[k] >= DISMISS_TTL_MS) delete dismissed[k];
    });
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(dismissed));
  } catch {}
}

const T = {
  es: { days: "días", hours: "h", minutes: "min", seconds: "seg", defaultCta: "Regístrate", close: "Cerrar", starts: "Comienza el" },
  fr: { days: "jours", hours: "h", minutes: "min", seconds: "sec", defaultCta: "Inscription", close: "Fermer", starts: "Commence le" },
  en: { days: "days", hours: "h", minutes: "min", seconds: "sec", defaultCta: "Register", close: "Close", starts: "Starts on" },
  ar: { days: "أيام", hours: "س", minutes: "د", seconds: "ث", defaultCta: "التسجيل", close: "إغلاق", starts: "يبدأ في" },
};

function pickField(event, base, lang) {
  return event?.[`${base}_${lang}`] || event?.[`${base}_es`] || "";
}

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return { done: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { done: false, days, hours, minutes, seconds };
}

/**
 * Home-page popup shown when an admin flags an upcoming Event with
 * `show_home_popup=true`. Fetches from `/events?home_popup=1` so the backend
 * already filters to published + future events.
 *
 * Dismiss state is remembered per-event in localStorage with a timestamp —
 * closing the popup for event X hides it for 24h on that browser, then it
 * becomes eligible to show again. A NEW popup-flagged event always shows
 * normally regardless of past dismissals.
 */
export default function HomePopup() {
  const { lang } = useLang();
  const L = T[lang] || T.es;
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/events?home_popup=1").then((r) => {
      if (cancelled) return;
      const list = r.data || [];
      // Show the FIRST upcoming popup-flagged event (sorted ascending by
      // starts_at on the backend). Ignore any the user dismissed in the
      // last 24h — after that it's fair game to show again.
      const next = list.find((e) => !isDismissed(e.id));
      if (next) {
        setEvent(next);
        // Small delay so the fade-in feels intentional
        setTimeout(() => setVisible(true), 400);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const countdown = useCountdown(event?.starts_at);

  // Global ESC handler for "easy to close". Registered BEFORE the early
  // return so hook order is stable across renders. Guarded by `visible` so
  // it's a no-op when the popup isn't showing.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setVisible(false);
      if (event?.id) markDismissed(event.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, event?.id]);

  if (!event || !visible || (countdown && countdown.done)) return null;

  const headline = pickField(event, "popup_headline", lang) || pickField(event, "title", lang);
  const ctaLabel = pickField(event, "popup_cta_label", lang) || L.defaultCta;
  const ctaUrl = (event.popup_cta_url && event.popup_cta_url.trim()) || `/actividades/${event.slug}`;

  const close = () => {
    setVisible(false);
    if (event?.id) markDismissed(event.id);
  };

  const isExternal = /^https?:\/\//i.test(ctaUrl);
  const startsFormatted = event.starts_at ? new Date(event.starts_at).toLocaleDateString(
    lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "ar" ? "ar-MA" : "en-US",
    { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }
  ) : "";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-500 overflow-y-auto"
      onClick={close}
      data-testid="home-popup"
      role="dialog"
      aria-modal="true"
      aria-label={headline}
    >
      {/* Backdrop X — always visible above the modal on desktop, offering a
          clear "escape" affordance separate from the small in-modal X. */}
      <button
        onClick={close}
        aria-label={L.close}
        className="hidden sm:flex fixed top-4 right-4 z-30 items-center gap-2 bg-white/95 hover:bg-white text-[#0F2040] px-4 h-11 rounded-full shadow-lg ring-1 ring-black/10 active:scale-95 transition text-sm font-semibold"
      >
        <X className="w-4 h-4" />
        <span>{L.close}</span>
        <span className="hidden md:inline text-[10px] font-normal text-gray-500 border border-gray-300 rounded px-1.5 py-0.5 ml-1">ESC</span>
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        dir={lang === "ar" ? "rtl" : "ltr"}
        className="relative bg-white max-w-3xl w-full my-auto grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] overflow-hidden shadow-2xl rounded-xl animate-in zoom-in-95 duration-500 max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-3rem)]"
      >
        {/* In-modal X — kept for mobile (where the backdrop X is hidden) and
            as a fallback within the modal on desktop. */}
        <button
          onClick={close}
          aria-label={L.close}
          data-testid="home-popup-close"
          className="sm:hidden absolute top-2 right-2 z-20 bg-white text-[#0F2040] w-11 h-11 rounded-full flex items-center justify-center shadow-lg ring-1 ring-black/10 hover:bg-gray-50 active:scale-95 transition"
          style={lang === "ar" ? { right: "auto", left: "8px" } : undefined}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="md:contents overflow-y-auto md:overflow-visible">
        {event.cover_image ? (
          // Mobile: compact 16:9 thumbnail with `object-cover` — posters lose
          //   nothing meaningful at that size.
          // Desktop: full-height column with `object-contain` on a dark
          //   backdrop → posters/portrait art render 100% visible, no crop.
          <div className="aspect-[16/9] md:aspect-auto overflow-hidden bg-gradient-to-br from-[#0F2040] to-[#1A2F5C] md:min-h-[24rem] flex items-center justify-center">
            <img
              src={event.cover_image}
              alt=""
              className="w-full h-full object-cover md:object-contain"
              loading="eager"
            />
          </div>
        ) : (
          <div className="hidden md:block bg-gradient-to-br from-[#0F2040] to-[#1A2F5C]" />
        )}

        <div className="p-6 sm:p-7 md:p-9 flex flex-col justify-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C8102E] mb-3">
            {L.starts}
          </p>
          <h3 className="font-serif text-2xl md:text-3xl text-[#0F2040] leading-tight mb-3" data-testid="home-popup-title">
            {headline}
          </h3>
          {startsFormatted && (
            <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 shrink-0" /> {startsFormatted}
            </p>
          )}
          {event.location && (
            <p className="text-sm text-gray-600 flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 shrink-0" /> {event.location}
            </p>
          )}

          {countdown && !countdown.done && (
            <div className="grid grid-cols-4 gap-2 my-5" data-testid="home-popup-countdown">
              {[
                { v: countdown.days, l: L.days },
                { v: countdown.hours, l: L.hours },
                { v: countdown.minutes, l: L.minutes },
                { v: countdown.seconds, l: L.seconds },
              ].map((c, i) => (
                <div key={i} className="text-center bg-[#0F2040] text-white py-2.5">
                  <div className="font-serif text-2xl leading-none" dir="ltr">{String(c.v).padStart(2, "0")}</div>
                  <div className="text-[9px] uppercase tracking-widest text-white/70 mt-1">{c.l}</div>
                </div>
              ))}
            </div>
          )}

          {isExternal ? (
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              data-testid="home-popup-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#A30D25] text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <Link
              to={ctaUrl}
              onClick={close}
              data-testid="home-popup-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#A30D25] text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
